import { useMemo } from 'react';
import { routing } from '@utils/routing';
import { DeliveryTruckIcon, ExclamationMarkIcon, HeartIcon } from '@fourthwall/icons';
import { useCurrentShopDashboardQuery } from '@modules/Dashboard/hooks/useCurrentShopDashboardQuery';
import { useDashboardPermissions } from '@components/utils/DashboardPermissions';
import { useDnsQuery } from '@modules/Dashboard/hooks/useDnsQuery';
import { pluralize } from '@fourthwall/utils/dist/string';
import { useKudosOrdersCountQuery } from '@modules/Dashboard/Home/queries/useKudosOrdersCountQuery';
import dayjs from 'dayjs';
import { useOrdersStatisticsQuery } from '@hooks/useOrdersQuery/useOrdersStatisticsQuery';
const DAYS_LOOKBACK = 90;
const ninetyDaysAgo = dayjs().subtract(DAYS_LOOKBACK, 'day').format('YYYY-MM-DDTHH:mm:ssZ');
const staleTime = 60_000; // 1 minute
export const useActionsBars = () => {
    const { dashboardPermissions, hasPermission } = useDashboardPermissions();
    const { currentShopDashboardQuery } = useCurrentShopDashboardQuery();
    const { dnsQuery } = useDnsQuery();
    const { kudosOrdersCountQuery } = useKudosOrdersCountQuery([{ state: ['WAITING'] }], {
        staleTime,
    });
    const { getOrdersStatisticsQuery } = useOrdersStatisticsQuery([{ types: ['COMMON'], updatedAtIsAfter: ninetyDaysAgo }], {
        staleTime: Infinity,
        enabled: hasPermission('contributions.thankYous'),
    });
    const hasOrderInLastNinetyDays = getOrdersStatisticsQuery.data && getOrdersStatisticsQuery.data.value > 0;
    const { ordersToManuallyFulfill = 0 } = currentShopDashboardQuery.data ?? {};
    const topSupportersCount = kudosOrdersCountQuery.data?.count || 0;
    const domainDnsRecords = dnsQuery.data;
    const actionsBars = useMemo(() => [
        {
            id: 'fulfillment_orders_to_manually_fulfill',
            enabled: hasPermission('home.fulfillments') && (ordersToManuallyFulfill ?? 0) > 0,
            label: `${ordersToManuallyFulfill} ${pluralize(ordersToManuallyFulfill, 'order')} left to send`,
            to: routing.contributions.fulfillment,
            appearance: 'warning',
            icon: DeliveryTruckIcon,
        },
        {
            id: 'thank_top_supporters',
            enabled: dashboardPermissions.contributions.thankYous &&
                !!topSupportersCount &&
                hasOrderInLastNinetyDays,
            to: routing.contributions.thankYous.self,
            label: `Thank your top supporters`,
            icon: HeartIcon,
        },
        {
            id: 'verify_domain',
            enabled: !!domainDnsRecords &&
                domainDnsRecords?.dnsList !== null &&
                !domainDnsRecords?.dnsList?.isEachWebsiteEntryVerified,
            to: routing.settings.domain.configuration,
            label: 'Verify your domain',
            icon: ExclamationMarkIcon,
        },
    ].filter((action) => action.enabled), [
        hasPermission,
        ordersToManuallyFulfill,
        dashboardPermissions.contributions.thankYous,
        topSupportersCount,
        hasOrderInLastNinetyDays,
        domainDnsRecords,
    ]);
    return {
        actionsBars,
    };
};
